import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Modal, Table, Spinner } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';
import settingsStore from '../../store/SettingsStore';
import toastStore from '../../store/ToastStore';
import ErrorAlert from '../UI/Status/ErrorAlert.jsx';
import DigestSettingsModal from './DigestSettingsModal.jsx';
import CloudApiSettingsModal from './CloudApiSettingsModal.jsx';
import ChannelCommissionsModal from './ChannelCommissionsModal.jsx';

function extractErrorMessage(e, fallback) {
  return e?.response?.data?.message || e?.message || fallback;
}

const ServerForm = observer(() => {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [serverAddress, setServerAddress] = useState('');
  const [name, setName] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [digestTarget, setDigestTarget] = useState(null);
  const [cloudTarget, setCloudTarget] = useState(null);
  const [commissionsTarget, setCommissionsTarget] = useState(null);

    useEffect(() => {
    settingsStore.getServers(localStorage.getItem('userId'));
  }, []);

  const handleDelete = (server) => {
    setDeleteTarget(server);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    const id = deleteTarget?.id;
    const label = deleteTarget?.name || deleteTarget?.server || 'сервер';
    if (!id) {
      setShowDeleteModal(false);
      setDeleteTarget(null);
      return;
    }
    try {
      await settingsStore.delteServer(id);
      toastStore.success(`Сервер «${label}» удалён.`);
      setShowDeleteModal(false);
      setDeleteTarget(null);
      await settingsStore.getServers(localStorage.getItem('userId'));
    } catch (e) {
      toastStore.error(extractErrorMessage(e, `Не удалось удалить «${label}».`));
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!login.trim()) errors.login = 'Укажите логин.';
    if (!password.trim()) errors.password = 'Укажите пароль.';
    if (!serverAddress.trim()) errors.serverAddress = 'Укажите адрес сервера.';
    if (!name.trim()) errors.name = 'Укажите название заведения.';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setLogin('');
    setPassword('');
    setServerAddress('');
    setName('');
    setFieldErrors({});
  };

  const handleAddServer = async () => {
    if (!validateForm()) return;
    try {
      await settingsStore.postIikoServer(
        serverAddress,
        password,
        login,
        localStorage.getItem('userId'),
        name,
      );
      toastStore.success(`Сервер «${name}» добавлен.`);
      setShowModal(false);
      resetForm();
      await settingsStore.getServers(localStorage.getItem('userId'));
    } catch (e) {
      toastStore.error(extractErrorMessage(e, 'Не удалось добавить сервер.'));
    }
  };

  const handleCloseModal = () => {
    if (settingsStore.addPending) return;
    setShowModal(false);
    resetForm();
  };

  return (
    <Container className="mt-5" style={{flex: "1"}}>
      <ErrorAlert error={settingsStore.error} title="Ошибка загрузки серверов" />
      <Row className="mb-3">
        <Col>
          <Button variant="primary" onClick={() => setShowModal(true)}>
            Добавить сервер
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          {settingsStore.serversLoading ? (
            <div className="d-flex align-items-center gap-2 text-body-secondary py-4">
              <Spinner animation="border" size="sm" />
              Загрузка серверов…
            </div>
          ) : settingsStore.servers.length === 0 ? (
            <p className="text-body-secondary mb-0">
              Нет подключённых серверов iiko. Нажмите «Добавить сервер», чтобы подключить первый.
            </p>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Имя</th>
                  <th>Адрес сервера</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {settingsStore.servers.map((server) => (
                  <tr key={server.id}>
                    <td>{server.name}</td>
                    <td>{server.server}</td>
                    <td className="d-flex gap-2">
                      <Button variant="outline-primary" onClick={() => setDigestTarget(server)}>
                        Дайджест
                      </Button>
                      <Button variant="outline-secondary" onClick={() => setCloudTarget(server)}>
                        iikoCloud
                      </Button>
                      <Button variant="outline-secondary" onClick={() => setCommissionsTarget(server)}>
                        Комиссии
                      </Button>
                      <Button variant="danger" onClick={() => handleDelete(server)}>
                        Удалить
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Col>
      </Row>

      <Modal show={showDeleteModal} onHide={() => !settingsStore.deletePending && setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Подтвердите удаление</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Вы точно хотите удалить сервер{" "}
          <strong>{deleteTarget?.name || deleteTarget?.server || ""}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={settingsStore.deletePending}>
            Отмена
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={settingsStore.deletePending}>
            {settingsStore.deletePending ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Удаление…
              </>
            ) : (
              "Удалить"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Добавить сервер</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formLogin" className="mb-3">
              <Form.Label>Логин</Form.Label>
              <Form.Control
                type="text"
                placeholder="Введите логин"
                value={login}
                isInvalid={!!fieldErrors.login}
                onChange={(e) => setLogin(e.target.value)}
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.login}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="formPassword" className="mb-3">
              <Form.Label>Пароль</Form.Label>
              <Form.Control
                type="password"
                placeholder="Введите пароль"
                value={password}
                isInvalid={!!fieldErrors.password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.password}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="formServerAddress" className="mb-3">
              <Form.Label>Адрес сервера</Form.Label>
              <Form.Control
                type="text"
                placeholder="Введите адрес сервера"
                value={serverAddress}
                isInvalid={!!fieldErrors.serverAddress}
                onChange={(e) => setServerAddress(e.target.value)}
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.serverAddress}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="formName">
              <Form.Label>Заведение</Form.Label>
              <Form.Control
                type="text"
                placeholder="Введите название заведения"
                value={name}
                isInvalid={!!fieldErrors.name}
                onChange={(e) => setName(e.target.value)}
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.name}</Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal} disabled={settingsStore.addPending}>
            Закрыть
          </Button>
          <Button variant="primary" onClick={handleAddServer} disabled={settingsStore.addPending}>
            {settingsStore.addPending ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Добавление…
              </>
            ) : (
              "Добавить"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <DigestSettingsModal
        server={digestTarget}
        show={!!digestTarget}
        onClose={() => setDigestTarget(null)}
      />
      <CloudApiSettingsModal
        server={cloudTarget}
        show={!!cloudTarget}
        onClose={() => setCloudTarget(null)}
      />
      <ChannelCommissionsModal
        server={commissionsTarget}
        show={!!commissionsTarget}
        onClose={() => setCommissionsTarget(null)}
      />
    </Container>
  );
});

export default ServerForm;
